import { Outlet, Link } from 'react-router-dom';

import { LogoBadge } from 'components';
import { PATHS } from 'routes/routePaths';
import AuthShowcase from './components/AuthShowcase';

/**
 * AuthLayout — premium split-screen shell for login / register / forgot.
 *
 * Left (lg+): a deep indigo editorial panel with a serif-italic headline and a
 * student testimonial — the "Direction A" treatment from the design board.
 * Right: a clean white panel holding the brand mark and the routed form (Outlet).
 * Collapses to a single column (form only) with a slim trust strip on mobile.
 */
export default function AuthLayout() {
  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      {/* ---- Brand panel (lg+, left) — indigo gradient ---- */}
      <aside className="relative hidden overflow-hidden bg-auth-panel lg:flex lg:flex-col lg:justify-between">
        {/* Mesh glow — top-left bright spot */}
        <div className="pointer-events-none absolute -left-20 -top-20 h-[28rem] w-[28rem] rounded-full bg-white/10 blur-[80px]" />
        {/* Mesh glow — bottom-right warm gold accent */}
        <div className="pointer-events-none absolute -bottom-24 -right-12 h-72 w-72 rounded-full bg-gold-400/15 blur-3xl" />
        {/* Dot-grid texture overlay */}
        <div className="pointer-events-none absolute inset-0 bg-grid opacity-30" />

        {/* Logo */}
        <div className="relative z-10 px-12 pt-12">
          <Link to={PATHS.LANDING} aria-label="LearnX home" className="rounded-xl focus-ring">
            <LogoBadge size="lg" />
          </Link>
        </div>

        {/* Headline + product preview */}
        <div className="relative z-10 px-12">
          <p className="mb-4 text-2xs font-bold uppercase tracking-[0.18em] text-white/70">
            One-to-one mentoring
          </p>
          <h2 className="max-w-md font-display text-display-md font-extrabold leading-[1.08] text-white">
            Learn from mentors who&rsquo;ve{' '}
            <span className="font-editorial font-normal text-gold-200">actually been there</span>.
          </h2>
          <p className="mt-5 max-w-sm text-base leading-relaxed text-white/65">
            One-to-one sessions with tutors who&rsquo;ve earned places at Oxford, Cambridge &amp;
            the Ivy League.
          </p>

          {/* App preview */}
          <div className="mt-12 mb-6 pr-6">
            <AuthShowcase />
          </div>
        </div>

        {/* Testimonial strip */}
        <div className="relative z-10 border-t border-white/15 px-12 py-6">
          <figure className="flex items-center gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white/20 text-xs font-semibold text-white ring-1 ring-white/25">
              SC
            </span>
            <figcaption className="text-sm leading-snug">
              <span className="font-editorial text-white/90">
                &ldquo;My mentor mapped out my entire Common App.&rdquo;
              </span>{' '}
              <span className="text-white/55">— Sofia C., now at Yale &rsquo;28</span>
            </figcaption>
          </figure>
        </div>
      </aside>

      {/* ---- Form panel (right / mobile) ---- */}
      <div className="relative flex flex-col bg-white px-6 py-8 sm:px-10">
        <header className="flex items-center justify-between">
          {/* On lg the indigo panel already shows the logo — keep header spacing but hide. */}
          <Link
            to={PATHS.LANDING}
            aria-label="LearnX home"
            className="rounded-xl focus-ring lg:invisible"
          >
            <LogoBadge size="md" />
          </Link>
          <Link
            to={PATHS.LANDING}
            className="text-sm font-medium text-slate-500 transition-colors hover:text-slate-900"
          >
            Back to home
          </Link>
        </header>

        <main className="flex flex-1 items-center justify-center py-10">
          <div className="w-full max-w-md animate-fade-in-up">
            <Outlet />
          </div>
        </main>

        {/* Mobile-only trust strip (the indigo panel is hidden below lg). */}
        <p className="text-center text-xs text-slate-400 lg:hidden">
          🔒 Trusted by families across the US &amp; UK · Safe, private 1-on-1 learning
        </p>
      </div>
    </div>
  );
}
